import {
  Box,
  Popper,
  TableCell,
  TableRow,
  Typography,
  styled,
  useTheme
} from '@mui/material';
import {
  useMemo,
  useState
} from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { GlAccountFragFragment } from 'generated/graphql';
import _ from 'lodash';
import { BlockMath } from 'react-katex';



const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));


export type CompanyFinancialMetricsTableRowProps = {
  years: number[];
  glAccount: GlAccountFragFragment;
  summary: 'cagr' | 'avg';
};

export const FC = (props: CompanyFinancialMetricsTableRowProps) => {
  const theme = useTheme();
  const [
    width,
    setWidth
  ] = useState(0);

  const cagr = props.glAccount.fiveYearCompoundAnnualGrowthRate;
  const avg = props.glAccount.fiveYearAverage;
  const summaryValue = props.summary === 'cagr' ? cagr : avg;
  const formattedSummary = props.summary === 'cagr' ? cagr?.toFixed(2) + ' %' : props.glAccount.formattedFiveYearAverage;

  const valuesToShow = useMemo(() => props.years.map((year) => {
    const value = props.glAccount.fiscalYearValues.find((v) => v.year === year);
    return value ?? null;
  }), [
    props.glAccount,
    props.years
  ]);

  const [
    anchorEl,
    setAnchorEl
  ] = useState<HTMLElement | null>(null);

  const __spaceConverter = (formula: string): string => formula.replaceAll(' ', '\\ ');

  return (
    <StyledTableRow>
      {
        valuesToShow.map((node, i) => {
          if (_.isNull(node)) {
            return (
              <TableCell
                key={i}
                sx={{
                }}
              >
                -
              </TableCell>
            );
          }

          if (_.isString(node)) {
            return (
              <TableCell key={i}>
                {node}
              </TableCell>
            );
          }

          return (
            <TableCell
              key={i}
              sx={{
                color: node.value.lt(0)
                  ? theme.palette.error.main
                  : undefined
              }}
            >
              {node.formattedValueForFinance}
            </TableCell>
          );
        })
      }
      <TableCell
        variant='rowLabel'
        sx={{
          width: '14rem',
          textAlign: 'right'
        }}
      >
        {props.glAccount.financialsDisplayName}
        <InfoOutlinedIcon
          fontSize='small'
          sx={{
            cursor: 'pointer',
            ml: 0.5,
            verticalAlign: 'middle',
            color: 'action.active'
          }}
          onMouseEnter={(ev) => setAnchorEl(ev.currentTarget as unknown as HTMLElement)}
          onMouseLeave={() => {
            setTimeout(() => {
              if (!document.querySelector('.popper-content:hover')) {
                setAnchorEl(null);
              }
            }, 100);
          }}
        />
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement='top-start'
          sx={{
            zIndex: 1500,
            p: 1,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 3
          }}
          onMouseEnter={() => clearTimeout}
          onMouseLeave={() => setAnchorEl(null)}
        >
          <Box>
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 'bold',
                fontSize: '18px',
                maxWidth: width || 400
              }}
            >
              {props.glAccount.displayName}
            </Typography>

            <Typography
              variant='body2'
              sx={{
                maxWidth: width || 400,
                fontFamily: 'Roboto',
                textTransform: 'none',
                fontSize: '14px'
              }}
            >
              {props.glAccount.definition}
            </Typography>

            {props.glAccount.formula
              ? (
                <Box
                  ref={(ref) => {
                    // The width of the content above this container should be dictated by the width
                    // of this container.
                    const clientWidth = (ref as any)?.clientWidth;
                    if (clientWidth) {
                      setWidth(clientWidth);
                    }
                  }}
                  sx={{
                    fontSize: '12px'
                  }}
                >
                  <BlockMath math={__spaceConverter(props.glAccount.formula)} />
                </Box>
              )
              : (
                <Typography
                  variant='body1'
                  sx={{
                    textTransform: 'none',
                    fontStyle: 'italic',
                    fontSize: '0.8rem',
                    fontWeight: 'normal',
                    mt: 0.5
                  }}
                >
                  (This value is reported)
                </Typography>
              )}
          </Box>
        </Popper>
      </TableCell>

      {(!_.isNil(summaryValue) && !_.isNaN(summaryValue)) ? (
        <TableCell
          width='300px'
          align='right'
          sx={{
            color: summaryValue.lt(0)
              ? theme.palette.error.main
              : undefined
          }}
        >
          {formattedSummary}
        </TableCell>
      ) : (
        <TableCell
          width='300px'
          align='right'
        >
          -
        </TableCell>
      )}
    </StyledTableRow>
  );
};
